import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Search,
  Pencil,
  Trash2,
  Tag,
  Loader2,
  RefreshCw,
  Database,
  Save,
  Link,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Trash,
  Filter,
  ChevronDown,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { EntityLink } from "../../components/ui/EntityLink";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { genresApi, adminApi } from "../../utils/api";
import { parseCommaSeparated, arraysEqual } from "../../utils/helpers";
import { useEntityDeepLink } from "../../hooks/useEntityDeepLink";
import GenreSpacePanel from "./GenreSpacePanel";

const PAGE_SIZE = 20;
const JOB_POLL_INTERVAL = 3000;
// Sentinel value matching the backend's UNKNOWN_DELETER_ID — represents
// soft-deleted genres that have no `deleted_by` field (deleted before tracking
// was added, or via tools that don't record an admin user).
const UNKNOWN_DELETER_ID = "__unknown__";

function DeleterMultiSelect({ deleters, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const allIds = useMemo(() => deleters.map((d) => d.user_id), [deleters]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));

  const toggleOne = (id) => {
    if (selected.includes(id)) onChange(selected.filter((s) => s !== id));
    else onChange([...selected, id]);
  };

  const selectAll = () => {
    if (!allSelected) onChange(allIds);
  };

  const getDeleterLabel = (d) => d.user_id === UNKNOWN_DELETER_ID ? "No deleter recorded" : (d.email || d.user_id);

  const buttonLabel = allSelected
    ? "All deleters"
    : selected.length === 0
      ? "No deleters selected"
      : selected.length === 1
        ? getDeleterLabel(deleters.find((d) => d.user_id === selected[0]) || { user_id: selected[0], email: null })
        : `${selected.length} deleters selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        title="Filter by who deleted the genre"
      >
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="truncate max-w-[14rem]">{buttonLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-background shadow-lg">
          <button
            type="button"
            onClick={selectAll}
            disabled={allSelected}
            className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-sm font-medium hover:bg-muted/40 disabled:cursor-default disabled:text-muted-foreground disabled:hover:bg-transparent"
          >
            <span>Select all</span>
            {allSelected && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
          </button>
          <div className="max-h-64 overflow-y-auto py-1">
            {deleters.length === 0 && (
              <p className="px-3 py-2 text-xs text-muted-foreground">No deleters yet</p>
            )}
            {deleters.map((d) => (
              <label
                key={d.user_id}
                className="flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer hover:bg-muted/40 sm:py-1.5"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(d.user_id)}
                  onChange={() => toggleOne(d.user_id)}
                  className="h-4 w-4 accent-primary"
                />
                <span className={`truncate ${d.user_id === UNKNOWN_DELETER_ID ? "italic text-muted-foreground" : ""}`}>
                  {getDeleterLabel(d)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function JobStatusModal({ job, onDismiss, onClose }) {
  const done = job.status === "succeeded" || job.status === "failed";
  const result = job.result_json || null;
  // Bulk result shape: { deleted, failed, results: [...], errors: [{genre_id, genre_name, error}] }
  const isBulkResult = result && typeof result.deleted === "number" && typeof result.failed === "number";
  const deletedCount = isBulkResult ? result.deleted : 0;
  const failedCount = isBulkResult ? result.failed : 0;
  const hasPartialFailure = isBulkResult && failedCount > 0;
  const modalSize = isBulkResult && (deletedCount > 0 || failedCount > 0) ? "max-w-md" : "max-w-sm";

  return (
    <Modal open={true} onOpenChange={onDismiss}>
      <ModalContent title={job.label || "Background Job"} className={modalSize}>
        <div className="flex flex-col items-center gap-4 py-4">
          {!done ? (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground capitalize">{job.status || "running"}…</p>
            </>
          ) : job.status === "succeeded" ? (
            isBulkResult ? (
              <>
                {hasPartialFailure
                  ? <XCircle className="h-10 w-10 text-amber-500" />
                  : <CheckCircle className="h-10 w-10 text-green-600" />}
                <p className="text-sm font-medium">{deletedCount} succeeded · {failedCount} failed</p>
                {hasPartialFailure && (
                  <div className="w-full rounded-md bg-amber-500/10 border border-amber-500/20 p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs font-medium text-amber-800 mb-1">Failed:</p>
                    <ul className="space-y-1 text-xs text-amber-900">
                      {(result.errors || []).map((e) => (
                        <li key={e.genre_id}>
                          <span className="font-medium">{e.genre_name || e.genre_id}</span>
                          {e.error && <span className="text-muted-foreground"> — {e.error}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <CheckCircle className="h-10 w-10 text-green-600" />
                <p className="text-sm font-medium">Completed successfully</p>
              </>
            )
          ) : (
            <>
              <XCircle className="h-10 w-10 text-destructive" />
              <p className="text-sm font-medium text-destructive">Job failed</p>
              {job.error && (
                <p className="text-xs text-muted-foreground text-center">{job.error}</p>
              )}
            </>
          )}
        </div>
        <ModalFooter>
          {done
            ? <Button onClick={onClose}>Close</Button>
            : <Button variant="outline" onClick={onDismiss}>Dismiss — run in background</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function WikidataBadge({ genre }) {
  const entityId = genre?.source_ids?.wikidata;
  if (entityId) {
    return (
      <span
        title={`Confirmed: ${entityId}`}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-700 border border-green-500/20"
      >
        <CheckCircle className="h-3 w-3" /> {entityId}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground border border-border">
      <XCircle className="h-3 w-3" /> Unconfirmed
    </span>
  );
}

export default function GenresPage() {
  const [genres, setGenres] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [confirmedFilter, setConfirmedFilter] = useState(null); // null | true | false
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Detail modal
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [wikidataDetail, setWikidataDetail] = useState(null);
  const [wikidataDetailLoading, setWikidataDetailLoading] = useState(false);

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Delete modal (always confirm; handles both fast-delete and reembed-job)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Genre artists panel (inside detail modal)
  const [genreArtists, setGenreArtists] = useState([]);
  const [genreArtistsTotal, setGenreArtistsTotal] = useState(0);
  const [genreArtistsLoading, setGenreArtistsLoading] = useState(false);
  const [genreArtistsPage, setGenreArtistsPage] = useState(1);
  const GENRE_ARTISTS_PAGE_SIZE = 10;

  // Wikidata link modal
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [wikidataSearch, setWikidataSearch] = useState("");
  const [wikidataResults, setWikidataResults] = useState([]);
  const [wikidataLoading, setWikidataLoading] = useState(false);
  const [selectedWikidataEntry, setSelectedWikidataEntry] = useState(null);
  const [isLinking, setIsLinking] = useState(false);

  // Confirmed / unconfirmed / deleted totals (always shown regardless of active filter)
  const [confirmedCount, setConfirmedCount] = useState(null);
  const [unconfirmedCount, setUnconfirmedCount] = useState(null);
  const [deletedCount, setDeletedCount] = useState(null);

  // Job status modal (genre-delete)
  const [activeJob, setActiveJob] = useState(null); // { job_id, type, status, label }
  const pollRef = useRef(null);

  // Bulk selection
  const [selectedGenreIds, setSelectedGenreIds] = useState(new Set());
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Deleted genres view
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedGenres, setDeletedGenres] = useState([]);
  const [deletedPagination, setDeletedPagination] = useState(null);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedPage, setDeletedPage] = useState(1);
  const [deletedSearchInput, setDeletedSearchInput] = useState("");
  const [deletedSearchFilter, setDeletedSearchFilter] = useState("");
  const [deletedByFilter, setDeletedByFilter] = useState([]); // [] = all selected (no filter)
  const [deleters, setDeleters] = useState([]);
  // null = never loaded; Set otherwise. Distinguishes first load from subsequent loads
  // and tracks which IDs we've already seen so newly-appearing deleters get auto-ticked.
  const seenDeleterIdsRef = useRef(null);
  // Mirror of `deleters` for read-only access inside fetchDeletedGenres without
  // making it a callback dependency (which would re-create the callback on every
  // API response and trigger infinite re-fetches).
  const deletersRef = useRef([]);
  useEffect(() => { deletersRef.current = deleters; }, [deleters]);
  // Same treatment for the filter selection — when the user ticks/unticks a
  // deleter we drive the refetch from a dedicated effect, NOT a callback
  // dependency, so the auto-tick branch (which itself calls setDeletedByFilter)
  // doesn't re-create the callback and trigger a redundant in-flight refetch.
  const deletedByFilterRef = useRef([]);
  useEffect(() => { deletedByFilterRef.current = deletedByFilter; }, [deletedByFilter]);
  // One-shot suppression flag: the auto-tick branch inside fetchDeletedGenres
  // updates deletedByFilter programmatically (first load = tick everyone; later
  // refreshes = tick newly-arrived deleters). Those updates must NOT trigger
  // the filter-change effect below, otherwise initial load fires twice.
  // Set this to `true` right before any programmatic setDeletedByFilter; the
  // effect consumes the flag and skips exactly one firing.
  const skipNextFilterEffectRef = useRef(false);

  const fetchStats = useCallback(async () => {
    try {
      const data = await genresApi.getStats();
      setConfirmedCount(data.confirmed_count ?? null);
      setUnconfirmedCount(data.unconfirmed_count ?? null);
      setDeletedCount(data.deleted_count ?? null);
    } catch {
      // non-critical — leave counts as-is on failure
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fetchGenres = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (searchFilter) params.search = searchFilter;
      if (confirmedFilter !== null) params.confirmed = confirmedFilter;

      const data = await genresApi.getAll(params);
      setGenres(data.genres || []);
      setPagination(data.pagination || null);
    } catch (err) {
      if (!err.response) {
        setError("Could not connect to the server.");
      } else {
        setError(err.response?.data?.detail || "Error loading genres");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, searchFilter, confirmedFilter]);

  useEffect(() => { fetchGenres(); }, [fetchGenres]);

  const fetchDeletedGenres = useCallback(async () => {
    // Read the current filter selection from a ref so this callback isn't
    // re-created on every tick/auto-tick state change (see the dedicated
    // effect below that drives refetches when the selection changes).
    const currentFilter = deletedByFilterRef.current;
    // After initialization, an empty selection means "show nothing"
    if (seenDeleterIdsRef.current !== null && currentFilter.length === 0) {
      setDeletedGenres([]);
      setDeletedPagination(null);
      setDeletedLoading(false);
      return;
    }
    setDeletedLoading(true);
    try {
      const params = { page: deletedPage, page_size: PAGE_SIZE };
      if (deletedSearchFilter) params.search = deletedSearchFilter;
      // Skip the filter when no strict subset is chosen: empty (= show none, handled above)
      // or all deleters selected (= no filtering needed).
      const knownIds = deletersRef.current.map((d) => d.user_id);
      const allSelected = knownIds.length > 0 && knownIds.every((id) => currentFilter.includes(id));
      if (currentFilter.length > 0 && !allSelected) {
        params.deleted_by = currentFilter.join(",");
      }
      const data = await genresApi.getDeleted(params);
      setDeletedGenres(data.genres || []);
      setDeletedPagination(data.pagination || null);
      // Backend contract (see AdminDeletedGenresResponse): `deleters` is only populated
      // on page=1 — subsequent pages return an empty list and the client is expected
      // to reuse the dropdown state from the first page. So only update the dropdown
      // and seen-IDs ref when we're on page 1; otherwise we'd wipe the cached options.
      if (deletedPage === 1 && Array.isArray(data.deleters)) {
        setDeleters(data.deleters);
        const allIds = data.deleters.map((d) => d.user_id);
        if (seenDeleterIdsRef.current === null) {
          // First load: tick all deleters by default.
          seenDeleterIdsRef.current = new Set(allIds);
          skipNextFilterEffectRef.current = true;
          setDeletedByFilter(allIds);
        } else {
          // Subsequent page-1 refreshes: auto-tick deleters that didn't exist before
          // (another admin deleted a genre while this page was open). Leave
          // user-unticked items alone.
          const seen = seenDeleterIdsRef.current;
          const trulyNew = allIds.filter((id) => !seen.has(id));
          allIds.forEach((id) => seen.add(id));
          if (trulyNew.length > 0) {
            skipNextFilterEffectRef.current = true;
            setDeletedByFilter((prev) => [...prev, ...trulyNew]);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error loading deleted genres");
    } finally {
      setDeletedLoading(false);
    }
  }, [deletedPage, deletedSearchFilter]);

  useEffect(() => { if (showDeleted) fetchDeletedGenres(); }, [showDeleted, fetchDeletedGenres]);
  // Drive refetches on filter change here (instead of via callback deps) so the
  // auto-tick branch inside fetchDeletedGenres doesn't recreate the callback
  // and trigger an extra in-flight refetch. The skip flag short-circuits this
  // effect when the filter change came from auto-tick rather than a user click.
  useEffect(() => {
    if (skipNextFilterEffectRef.current) {
      skipNextFilterEffectRef.current = false;
      return;
    }
    if (showDeleted && seenDeleterIdsRef.current !== null) fetchDeletedGenres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedByFilter]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error]);

  // Job polling
  const startJobPoll = useCallback((jobId, label) => {
    setActiveJob({ job_id: jobId, type: "pending", label });
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const job = await adminApi.getJobStatus(jobId);
        setActiveJob((prev) => prev ? { ...prev, ...job } : null);
        if (job.status === "succeeded" || job.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          fetchGenres(true);
          fetchStats();
        }
      } catch {
        // keep polling on transient errors
      }
    }, JOB_POLL_INTERVAL);
  }, [fetchGenres, fetchStats]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchFilter(searchInput.trim());
    setSelectedGenreIds(new Set());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchFilter("");
    setPage(1);
    setSelectedGenreIds(new Set());
  };

  const handleStatusChange = (filterValue) => {
    if (filterValue === "deleted") {
      setShowDeleted(true);
      setDeletedPage(1);
      setSelectedGenreIds(new Set());
      setDeletedSearchInput("");
      setDeletedSearchFilter("");
      setDeletedByFilter([]);
      setDeleters([]);
      seenDeleterIdsRef.current = null;
    } else {
      setShowDeleted(false);
      setConfirmedFilter(filterValue);
      setPage(1);
      setSelectedGenreIds(new Set());
    }
  };

  const loadGenreArtists = useCallback(async (genreId, page) => {
    setGenreArtistsLoading(true);
    try {
      const data = await genresApi.getArtists(genreId, { page, page_size: GENRE_ARTISTS_PAGE_SIZE });
      setGenreArtists((prev) => page === 1 ? (data.artists || []) : [...prev, ...(data.artists || [])]);
      setGenreArtistsTotal(data.pagination?.total ?? 0);
    } catch {
      // non-critical
    } finally {
      setGenreArtistsLoading(false);
    }
  }, []);

  const handleOpenDetail = (genre) => {
    setSelectedGenre(genre);
    setWikidataDetail(null);
    setGenreArtists([]);
    setGenreArtistsTotal(0);
    setGenreArtistsPage(1);
    setIsDetailModalOpen(true);
    const entityId = genre?.source_ids?.wikidata;
    if (entityId) {
      setWikidataDetailLoading(true);
      genresApi.getWikidataEntry(entityId)
        .then((entry) => setWikidataDetail(entry))
        .catch(() => setWikidataDetail(null))
        .finally(() => setWikidataDetailLoading(false));
    }
    loadGenreArtists(genre.id, 1);
  };

  // Deep link: `?genre=<id>` (a genre badge or neighbour elsewhere in the
  // admin) opens that genre's detail modal.
  useEntityDeepLink("genre", async (genreId) => {
    try {
      const data = await genresApi.getById(genreId);
      // GET /admin/genres/{id} wraps the genre as { genre: {...} }
      handleOpenDetail({ id: genreId, ...(data?.genre ?? data) });
    } catch (err) {
      console.error("Error opening genre from deep link:", err);
      setError(err.response?.data?.detail || "Could not open the requested genre");
    }
  });

  const handleOpenEdit = (genre) => {
    setSelectedGenre(genre);
    setEditData({ aliases: (genre.aliases || []).join(", ") });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedGenre) return;
    setIsSaving(true);
    setError(null);
    try {
      const newAliases = parseCommaSeparated(editData.aliases);
      if (arraysEqual(newAliases, selectedGenre.aliases || [])) {
        setIsEditModalOpen(false);
        return;
      }
      const result = await genresApi.update(selectedGenre.id, { aliases: newAliases });
      setSuccess(`Genre updated: ${result.updated_fields?.join(", ") || "OK"}`);
      setIsEditModalOpen(false);
      setSelectedGenre(null);
      fetchGenres(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error updating genre");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDelete = (genre) => {
    setSelectedGenre(genre);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedGenre) return;
    setIsDeleting(true);
    setError(null);
    const deletedId = selectedGenre.id;
    const deletedName = selectedGenre.name;
    try {
      const result = await genresApi.deleteWithReembed(deletedId);
      setIsDeleteModalOpen(false);
      setSelectedGenre(null);
      setGenres((prev) => prev.filter((g) => g.id !== deletedId));
      startJobPoll(result.job_id, `Deleting "${deletedName}"`);
    } catch (err) {
      setError(err.response?.data?.detail || "Error starting delete job");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedGenreIds.size === 0) return;
    setIsBulkDeleting(true);
    setError(null);
    const ids = Array.from(selectedGenreIds);
    try {
      const result = await genresApi.bulkDeleteWithReembed(ids);
      setIsBulkDeleteModalOpen(false);
      setSelectedGenreIds(new Set());
      setGenres((prev) => prev.filter((g) => !ids.includes(g.id)));
      startJobPoll(result.job_id, `Deleting ${ids.length} genre${ids.length !== 1 ? "s" : ""}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Error starting bulk delete job");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleToggleSelect = (genreId) => {
    setSelectedGenreIds((prev) => {
      const next = new Set(prev);
      if (next.has(genreId)) next.delete(genreId);
      else next.add(genreId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedGenreIds.size === genres.length) {
      setSelectedGenreIds(new Set());
    } else {
      setSelectedGenreIds(new Set(genres.map((g) => g.id)));
    }
  };

  const handleDeletedSearch = (e) => {
    e.preventDefault();
    setDeletedPage(1);
    setDeletedSearchFilter(deletedSearchInput.trim());
  };

  // Wikidata link
  const handleOpenLink = (genre) => {
    setSelectedGenre(genre);
    setWikidataSearch(genre.name);
    setWikidataResults([]);
    setSelectedWikidataEntry(null);
    setIsLinkModalOpen(true);
    handleSearchWikidata(genre.name);
  };

  const handleSearchWikidata = async (term) => {
    const q = (term ?? wikidataSearch).trim();
    if (!q) return;
    setWikidataSearch(q);
    setWikidataLoading(true);
    setSelectedWikidataEntry(null);
    try {
      const data = await genresApi.getWikidataList({ search: q, page_size: 500 });
      setWikidataResults(data.entries || []);
    } catch {
      setWikidataResults([]);
    } finally {
      setWikidataLoading(false);
    }
  };

  const handleLinkToWikidata = async (entry) => {
    if (!selectedGenre) return;
    setIsLinking(true);
    setError(null);
    try {
      const result = await genresApi.linkToWikidata(selectedGenre.id, entry.entity_id);
      setSuccess(
        `"${selectedGenre.name}" linked to ${entry.entity_id}` +
        (result.alias_added ? " (name added as alias)" : "")
      );
      setIsLinkModalOpen(false);
      setSelectedGenre(null);
      setSelectedWikidataEntry(null);
      fetchGenres(true);
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || "Error linking genre");
    } finally {
      setIsLinking(false);
    }
  };

  const totalPages = pagination?.total_pages || 1;
  const totalGenres = (confirmedCount ?? 0) + (unconfirmedCount ?? 0);
  const deletedTotalPages = deletedPagination?.total_pages || 1;
  const allPageSelected = genres.length > 0 && selectedGenreIds.size === genres.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Genres</h2>
          <p className="text-muted-foreground">Manage the platform's music genres</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button variant="outline" onClick={fetchGenres} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Close</button>
        </div>
      )}
      {success && (
        <div className="p-3 rounded-md text-sm bg-green-500/10 text-green-700 border border-green-500/20">
          {success}
          <button className="ml-2 underline" onClick={() => setSuccess(null)}>Close</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent>
            <div className="text-2xl font-bold">{totalGenres}</div>
            <p className="text-xs text-muted-foreground">Total genres</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {confirmedCount ?? "—"}
            </div>
            <p className="text-xs text-muted-foreground">Confirmed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {unconfirmedCount ?? "—"}
            </div>
            <p className="text-xs text-muted-foreground">Unconfirmed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {deletedCount ?? "—"}
            </div>
            <p className="text-xs text-muted-foreground">Deleted</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="space-y-4">
          <form onSubmit={showDeleted ? handleDeletedSearch : handleSearch} className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder={showDeleted ? "Search deleted genres..." : "Search by name or alias..."}
                value={showDeleted ? deletedSearchInput : searchInput}
                onChange={(e) => showDeleted ? setDeletedSearchInput(e.target.value) : setSearchInput(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <Button type="submit"><Search className="mr-2 h-4 w-4" /> Search</Button>
            {(showDeleted ? deletedSearchFilter : searchFilter) && (
              <Button
                type="button"
                variant="ghost"
                onClick={showDeleted
                  ? () => { setDeletedSearchInput(""); setDeletedSearchFilter(""); setDeletedPage(1); }
                  : handleClearSearch
                }
              >
                Clear
              </Button>
            )}
          </form>

          {/* Status filter */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground font-medium">Status:</span>
            {[
              { label: "All", value: null, icon: null },
              { label: "Confirmed", value: true, icon: null },
              { label: "Unconfirmed", value: false, icon: null },
              { label: "Deleted", value: "deleted", icon: Trash },
            ].map(({ label, value, icon: Icon }) => (
              <button
                key={label}
                onClick={() => handleStatusChange(value)}
                className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                  value === "deleted"
                    ? showDeleted
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground border-border hover:bg-muted"
                    : !showDeleted && confirmedFilter === value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                {Icon && <Icon className="inline h-3 w-3 mr-1 -mt-0.5" />}
                {label}
              </button>
            ))}
          </div>

          {/* Deleted view: filter by deleter */}
          {showDeleted && deleters.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground font-medium">Deleted by:</span>
              <DeleterMultiSelect
                deleters={deleters}
                selected={deletedByFilter}
                onChange={(ids) => { setDeletedByFilter(ids); setDeletedPage(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk delete bar */}
      {!showDeleted && selectedGenreIds.size > 0 && (
        <div className="flex flex-col gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm sm:flex-row sm:items-center">
          <span className="font-medium text-destructive">{selectedGenreIds.size} genre{selectedGenreIds.size !== 1 ? "s" : ""} selected</span>
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsBulkDeleteModalOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete selected
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedGenreIds(new Set())}>
              Clear selection
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {showDeleted ? (
            /* ── Deleted genres view ── */
            deletedLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading deleted genres...</span>
              </div>
            ) : deletedGenres.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Trash className="h-12 w-12 mb-4" />
                <p className="text-lg font-medium">No deleted genres found</p>
              </div>
            ) : (
              <>
                {/* Mobile list */}
                <div className="divide-y divide-border lg:hidden">
                  {deletedGenres.map((genre) => (
                    <div key={genre.id} className="space-y-2 p-4 opacity-70">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Tag className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium line-through text-muted-foreground">{genre.name}</p>
                          <div className="mt-1">
                            <WikidataBadge genre={genre} />
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1 ml-14">
                        {(genre.deleted_by_email || genre.deleted_by) && (
                          <p>Deleted by: <span className="font-medium">{genre.deleted_by_email || genre.deleted_by}</span></p>
                        )}
                        {genre.deleted_at && (
                          <p>Deleted: <span className="font-medium">{new Date(genre.deleted_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span></p>
                        )}
                      </div>
                      {(genre.aliases || []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {(genre.aliases || []).slice(0, 5).map((alias) => (
                            <Badge key={alias} variant="outline" className="text-xs opacity-60">
                              {alias}
                            </Badge>
                          ))}
                          {(genre.aliases || []).length > 5 && (
                            <span className="text-xs text-muted-foreground">+{genre.aliases.length - 5}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <div className="hidden lg:block">
                  <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Genre</TableHead>
                      <TableHead>Wikidata</TableHead>
                      <TableHead>Deleted by</TableHead>
                      <TableHead>Deleted at</TableHead>
                      <TableHead>Aliases</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletedGenres.map((genre) => (
                      <TableRow key={genre.id} className="opacity-70">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                              <Tag className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <p className="font-medium line-through text-muted-foreground">{genre.name}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <WikidataBadge genre={genre} />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {genre.deleted_by_email || genre.deleted_by || <span className="italic">unknown</span>}
                          </span>
                        </TableCell>
                        <TableCell>
                          {genre.deleted_at ? (
                            <span className="text-sm text-muted-foreground">
                              {new Date(genre.deleted_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                              <span className="block text-xs">{new Date(genre.deleted_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {(genre.aliases || []).slice(0, 3).map((alias) => (
                              <Badge key={alias} variant="outline" className="text-xs opacity-60">{alias}</Badge>
                            ))}
                            {(genre.aliases || []).length > 3 && (
                              <span className="text-xs text-muted-foreground">+{genre.aliases.length - 3}</span>
                            )}
                            {(!genre.aliases || genre.aliases.length === 0) && (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </>
            )
          ) : (
            /* ── Active genres view ── */
            loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading genres...</span>
              </div>
            ) : genres.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Database className="h-12 w-12 mb-4" />
                <p className="text-lg font-medium">No genres found</p>
              </div>
            ) : (
              <>
                {/* Mobile list */}
                <div className="divide-y divide-border lg:hidden">
                  {genres.map((genre) => (
                    <div
                      key={genre.id}
                      className="space-y-3 p-4"
                      onClick={() => handleOpenDetail(genre)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && handleOpenDetail(genre)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-11 w-11 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Tag className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium">{genre.name}</p>
                          <div className="mt-1">
                            <WikidataBadge genre={genre} />
                          </div>
                        </div>
                        {genre.artist_count != null && (
                          <span className="rounded-md bg-muted/50 px-2 py-1 text-sm font-medium">
                            {genre.artist_count}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {(genre.aliases || []).slice(0, 5).map((alias) => (
                          <Badge key={alias} variant="outline" className="text-xs">
                            {alias}
                          </Badge>
                        ))}
                        {(genre.aliases || []).length > 5 && (
                          <span className="text-xs text-muted-foreground">+{genre.aliases.length - 5}</span>
                        )}
                        {(!genre.aliases || genre.aliases.length === 0) && (
                          <span className="text-xs text-muted-foreground">No aliases</span>
                        )}
                      </div>

                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedGenreIds.has(genre.id)}
                          onChange={() => handleToggleSelect(genre.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-border"
                          aria-label={`Select ${genre.name}`}
                        />
                        {!genre.source_ids?.wikidata && (
                          <Button variant="ghost" size="icon" onClick={() => handleOpenLink(genre)} title="Link to Wikidata">
                            <Link className="h-4 w-4 text-blue-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(genre)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(genre)} title="Delete">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <div className="hidden lg:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <input
                            type="checkbox"
                            checked={allPageSelected}
                            onChange={handleSelectAll}
                            className="h-4 w-4 rounded border-border"
                            aria-label="Select all on page"
                          />
                        </TableHead>
                        <TableHead>Genre</TableHead>
                        <TableHead>Wikidata</TableHead>
                        <TableHead>Aliases</TableHead>
                        <TableHead>Artists</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {genres.map((genre) => (
                        <TableRow
                          key={genre.id}
                          className={`cursor-pointer hover:bg-muted/50 ${selectedGenreIds.has(genre.id) ? "bg-muted/30" : ""}`}
                          onClick={() => handleOpenDetail(genre)}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedGenreIds.has(genre.id)}
                              onChange={() => handleToggleSelect(genre.id)}
                              className="h-4 w-4 rounded border-border"
                              aria-label={`Select ${genre.name}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Tag className="h-5 w-5 text-primary" />
                              </div>
                              <p className="font-medium">{genre.name}</p>
                            </div>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <WikidataBadge genre={genre} />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-[250px]">
                              {(genre.aliases || []).slice(0, 4).map((alias) => (
                                <Badge key={alias} variant="outline" className="text-xs">{alias}</Badge>
                              ))}
                              {(genre.aliases || []).length > 4 && (
                                <span className="text-xs text-muted-foreground">+{genre.aliases.length - 4}</span>
                              )}
                              {(!genre.aliases || genre.aliases.length === 0) && (
                                <span className="text-xs text-muted-foreground">No aliases</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {genre.artist_count != null ? (
                              <span className="text-sm font-medium">{genre.artist_count}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {!genre.source_ids?.wikidata && (
                                <Button variant="ghost" size="icon" onClick={() => handleOpenLink(genre)} title="Link to Wikidata">
                                  <Link className="h-4 w-4 text-blue-500" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(genre)} title="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(genre)} title="Delete">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {showDeleted ? (
        <Pagination page={deletedPage} totalPages={deletedTotalPages} onPageChange={setDeletedPage} />
      ) : (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {/* Detail Modal */}
      <Modal open={isDetailModalOpen} onOpenChange={(open) => { setIsDetailModalOpen(open); if (!open) { fetchGenres(true); fetchStats(); } }}>
        <ModalContent title="Genre Details" className="max-w-2xl">
          {selectedGenre && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Tag className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedGenre.name}</h3>
                  <div className="mt-1"><WikidataBadge genre={selectedGenre} /></div>
                </div>
              </div>

              <div className="grid gap-4 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground">ID</p>
                  <p className="font-mono text-xs truncate">{selectedGenre.id}</p>
                </div>
                {selectedGenre.artist_count != null && (
                  <div>
                    <p className="text-muted-foreground">Associated artists</p>
                    <p className="font-bold">{selectedGenre.artist_count}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Aliases</p>
                <div className="flex flex-wrap gap-1">
                  {(selectedGenre.aliases || []).map((alias) => (
                    <Badge key={alias} variant="outline">{alias}</Badge>
                  ))}
                  {(!selectedGenre.aliases || selectedGenre.aliases.length === 0) && (
                    <span className="text-muted-foreground text-sm">No aliases</span>
                  )}
                </div>
              </div>

              {/* Wikidata info for confirmed genres */}
              {selectedGenre.source_ids?.wikidata && (
                <div className="rounded-md border border-green-500/20 bg-green-500/5 p-3 space-y-2">
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">Wikidata</p>
                  {wikidataDetailLoading && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                    </div>
                  )}
                  {wikidataDetail && !wikidataDetailLoading && (() => {
                    const isAlias = wikidataDetail.canonical_name.toLowerCase() !== (selectedGenre.name || "").toLowerCase();
                    return (
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-muted-foreground text-xs">Canonical name:</span>
                          <span className="font-medium">{wikidataDetail.canonical_name}</span>
                          {isAlias && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-500/10 text-amber-700 border border-amber-500/20">
                              showing as alias
                            </span>
                          )}
                        </div>
                        {wikidataDetail.parents?.length > 0 && (
                          <div className="flex items-start gap-2 flex-wrap">
                            <span className="text-muted-foreground text-xs mt-0.5">Parents:</span>
                            <div className="flex flex-wrap gap-1">
                              {wikidataDetail.parents.map((qid) => {
                                const name = wikidataDetail.parent_names?.[qid];
                                return (
                                  <Badge key={qid} variant="outline" className="text-xs">
                                    {name ? `${name} ` : ""}
                                    <span className="font-mono text-muted-foreground">{qid}</span>
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {wikidataDetail.aliases?.length > 0 && (
                          <div className="flex items-start gap-2 flex-wrap">
                            <span className="text-muted-foreground text-xs mt-0.5">Also known as:</span>
                            <div className="flex flex-wrap gap-1">
                              {wikidataDetail.aliases.slice(0, 8).map((alias) => (
                                <Badge key={alias} variant="outline" className="text-xs">{alias}</Badge>
                              ))}
                              {wikidataDetail.aliases.length > 8 && (
                                <span className="text-xs text-muted-foreground">+{wikidataDetail.aliases.length - 8} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <GenreSpacePanel key={selectedGenre.id} genre={selectedGenre} />

              {/* Artists using this genre */}
              {(genreArtistsLoading || genreArtistsTotal > 0) && (
                <div>
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Artists ({genreArtistsTotal || selectedGenre.artist_count})
                  </p>
                  {genreArtistsLoading && genreArtists.length === 0 ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {genreArtists.map((artist) => (
                        <div key={artist.id} className="flex items-center gap-2 text-sm py-1 border-b last:border-0">
                          <EntityLink type="artist" id={artist.id} className="font-medium flex-1 truncate">
                            {artist.name}
                          </EntityLink>
                          {artist.country && (
                            <span className="text-xs text-muted-foreground shrink-0">{artist.country}</span>
                          )}
                        </div>
                      ))}
                      {genreArtists.length < genreArtistsTotal && (
                        <button
                          className="text-xs text-primary underline pt-1"
                          onClick={() => {
                            const next = genreArtistsPage + 1;
                            setGenreArtistsPage(next);
                            loadGenreArtists(selectedGenre.id, next);
                          }}
                          disabled={genreArtistsLoading}
                        >
                          {genreArtistsLoading ? "Loading…" : `Load more (${genreArtistsTotal - genreArtists.length} remaining)`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <ModalFooter className="border-t pt-4">
                {!selectedGenre.source_ids?.wikidata && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setIsDetailModalOpen(false);
                    handleOpenLink(selectedGenre);
                  }}>
                    <Link className="h-4 w-4 mr-2" /> Link to Wikidata
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => {
                  setIsDetailModalOpen(false);
                  handleOpenEdit(selectedGenre);
                }}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                <Button variant="outline" onClick={() => { setIsDetailModalOpen(false); setSelectedGenre(null); }}>
                  Close
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal open={isEditModalOpen} onOpenChange={(open) => { setIsEditModalOpen(open); if (!open) { fetchGenres(true); fetchStats(); } }}>
        <ModalContent title="Edit Genre" className="max-w-lg">
          {selectedGenre && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground mb-2">
                <strong>{selectedGenre.name}</strong> (name not editable)
              </div>
              <div>
                <label className="text-sm font-medium">Aliases</label>
                <textarea
                  value={editData.aliases}
                  onChange={(e) => setEditData((d) => ({ ...d, aliases: e.target.value }))}
                  rows={3}
                  placeholder="rock, rock and roll, rock & roll..."
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-1">Separated by comma</p>
              </div>
              <ModalFooter>
                <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setSelectedGenre(null); }} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSaving}>
                  {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save</>}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Bulk Delete Modal */}
      <Modal open={isBulkDeleteModalOpen} onOpenChange={setIsBulkDeleteModalOpen}>
        <ModalContent title="Delete Selected Genres">
          <div className="space-y-3">
            <p className="text-muted-foreground">
              Are you sure you want to delete <strong>{selectedGenreIds.size} genre{selectedGenreIds.size !== 1 ? "s" : ""}</strong>?
            </p>
            <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-3 text-sm text-amber-800">
              All affected artists, events, and users will be re-embedded. This runs as a single background job.
            </div>
            <ModalFooter className="mt-2">
              <Button variant="outline" onClick={() => setIsBulkDeleteModalOpen(false)} disabled={isBulkDeleting}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBulkDelete} disabled={isBulkDeleting}>
                {isBulkDeleting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting...</>
                  : `Delete ${selectedGenreIds.size} genre${selectedGenreIds.size !== 1 ? "s" : ""}`
                }
              </Button>
            </ModalFooter>
          </div>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <ModalContent title="Delete Genre">
          {selectedGenre && (
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Are you sure you want to delete <strong>"{selectedGenre.name}"</strong>?
              </p>
              {(selectedGenre.artist_count ?? 0) > 0 && (
                <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-3 text-sm text-amber-800">
                  This genre is used by <strong>{selectedGenre.artist_count} artist{selectedGenre.artist_count !== 1 ? "s" : ""}</strong>.
                  Deleting will re-embed all affected artists, events, and users. This runs as a background job.
                </div>
              )}
              <ModalFooter className="mt-2">
                <Button variant="outline" onClick={() => { setIsDeleteModalOpen(false); setSelectedGenre(null); }} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting...</> : "Delete"}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Link to Wikidata Modal */}
      <Modal open={isLinkModalOpen} onOpenChange={(open) => { setIsLinkModalOpen(open); if (!open) { fetchGenres(true); fetchStats(); } }}>
        <ModalContent title="Link to Wikidata" className="max-w-xl">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Search the Wikidata music genre catalog and select the matching entry for <strong>"{selectedGenre?.name}"</strong>.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="text"
                placeholder="Search by genre name or alias..."
                value={wikidataSearch}
                onChange={(e) => setWikidataSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearchWikidata()}
                className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button onClick={() => handleSearchWikidata()} disabled={wikidataLoading || !wikidataSearch.trim()}>
                {wikidataLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            {wikidataResults.length > 0 && (
              <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                {wikidataResults.map((entry) => {
                  const isSelected = selectedWikidataEntry?.entity_id === entry.entity_id;
                  return (
                    <button
                      key={entry.entity_id}
                      onClick={() => setSelectedWikidataEntry(isSelected ? null : entry)}
                      disabled={isLinking}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        isSelected
                          ? "bg-primary/10 border-l-2 border-primary"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm">{entry.canonical_name}</p>
                          {entry.aliases?.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Also: {entry.aliases.slice(0, 4).join(", ")}{entry.aliases.length > 4 ? "..." : ""}
                            </p>
                          )}
                          {entry.parents?.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Parent: {entry.parents.slice(0, 2).join(", ")}
                            </p>
                          )}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground flex-shrink-0">{entry.entity_id}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {wikidataSearch && !wikidataLoading && wikidataResults.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No results found. Try a different term.</p>
            )}

            <ModalFooter>
              <Button variant="outline" onClick={() => { setIsLinkModalOpen(false); setSelectedGenre(null); setSelectedWikidataEntry(null); }}>
                Cancel
              </Button>
              {selectedWikidataEntry && (
                <Button onClick={() => handleLinkToWikidata(selectedWikidataEntry)} disabled={isLinking}>
                  {isLinking
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Linking...</>
                    : <><Link className="h-4 w-4 mr-2" />Link to {selectedWikidataEntry.entity_id}</>
                  }
                </Button>
              )}
            </ModalFooter>
          </div>
        </ModalContent>
      </Modal>

      {/* Job Status Modal (genre-delete) */}
      {activeJob && (
        <JobStatusModal
          job={activeJob}
          // Dismiss runs in background — still refresh so the optimistic removal
          // in handleBulkDelete doesn't leave the list stale if the job hasn't
          // finished or partially fails.
          onDismiss={() => { setActiveJob(null); fetchGenres(); fetchStats(); }}
          onClose={() => { setActiveJob(null); fetchGenres(); fetchStats(); }}
        />
      )}
    </div>
  );
}
